/**
 * The control chart a statistical alarm fired on (plant-analytics S15), in the alarm event detail: the
 * rule the signal broke, when, and the chart itself on demand (the server's series, limits and signals:
 * `GET /v1/twin/spc/charts/{chart_id}/series`). The app has no page of its own for one chart, so the
 * chart link opens it here.
 */
import { useState } from "react";
import { format } from "date-fns";
import { Activity } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { errorDetailOf } from "@/lib/api/error";
import { spcSubChartLabel, useSpcSeries } from "@/lib/api/spc";
import { spcAlarmRuleLabel } from "@/lib/alarms/spc-condition";
import { SPCChart } from "@/lib/widgets/statistical/SPCChart";
import { spcSeriesInUnit } from "@/lib/api/spc-units";
import { useTagUnitOf } from "@/lib/alarms/tag-units";
import { useShownPrefs } from "@/lib/units/context";
import type { AlarmItem } from "@/lib/widgets";

export function SpcAlarmChartSection({ spc }: { spc: NonNullable<AlarmItem["spc"]> }) {
  const [open, setOpen] = useState(false);
  const at = spc.at ? new Date(spc.at) : null;
  return (
    <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-elevated/40 p-3">
      <header className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        <Activity className="size-3.5" />
        Control chart signal
      </header>
      {spc.rule && (
        <p className="text-sm text-on-surface">
          {spcAlarmRuleLabel(spc.rule)}
          {spc.subChart && <span className="text-on-surface-muted"> on {spcSubChartLabel(spc.subChart).toLowerCase()}</span>}
          {at && !Number.isNaN(at.getTime()) && (
            <span className="text-on-surface-muted"> at {format(at, "PPp")}</span>
          )}
        </p>
      )}
      {spc.signals != null && spc.signals > 1 && (
        <p className="text-[11px] text-on-surface-muted">This alarm covers {spc.signals} live signals.</p>
      )}
      <p className="text-[11px] text-on-surface-muted">Statistical alarms fire only on new live data, never on history.</p>
      <div>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? "Hide the control chart" : "Open the control chart"}
        </Button>
      </div>
      {open && <SpcAlarmChart chartId={spc.chartId} />}
    </section>
  );
}

function SpcAlarmChart({ chartId }: { chartId: string }) {
  const series = useSpcSeries(chartId);
  // The chart is the server's, in the unit its tag stores: moved into the org's shown unit, and named where it moved.
  const prefs = useShownPrefs();
  const tagUnit = useTagUnitOf()(series.data?.chart.tag_id ?? "");
  if (series.isLoading) return <SPCChart state={{ kind: "loading" }} height={200} />;
  if (series.data) {
    const shown = spcSeriesInUnit(series.data, tagUnit, prefs);
    return <SPCChart series={shown.series} unit={shown.series === series.data ? undefined : shown.unit} height={220} />;
  }
  const detail = series.error ? errorDetailOf(series.error) : null;
  return (
    <p className="text-[11px] text-on-surface-muted">
      {detail ? `The control chart could not be loaded: ${detail}` : "The control chart is not available here."}
    </p>
  );
}
